// ===================================================================
// subscriptionService.ts 
// ===================================================================


export enum SubscriptionTier {
  FREE = 'free',
  PREMIUM = 'premium'
}

// Interface pour la structure d'abonnement
export interface Subscription {
  tier: SubscriptionTier;
  features: string[];
  startDate: number;
  expiresAt: number | null;
  paymentId: string;
  billingPeriod?: 'monthly' | 'yearly';
  planId?: string;
}


export interface UserSubscriptionData {
  userId: string;
  subscription: Subscription;
  paymentHistory: Array<{
    date: number;
    amount: number;
    plan: string;
    status: string;
  }>;
}

class SubscriptionService {
  private userId: string = 'default_user';
  private currentSubscription: Subscription | null = null;

  // 🔒 SÉCURITÉ : le Premium n'est accordé que s'il a été confirmé par une
  // source serveur (backend verify-tier / paiement, Supabase, RevenueCat)
  // PENDANT cette session. Ce drapeau vit uniquement en mémoire : écrire un
  // faux 'user_subscription' premium dans localStorage puis recharger la
  // page ne suffit donc plus à débloquer les fonctionnalités payantes. Le
  // cache local ne sert plus qu'à l'affichage optimiste.
  private serverVerified = false;

  constructor() {
    this.loadSubscriptionFromStorage();
  }


  private getCurrentUserEmail(): string | null {
    try {
   
      const secureUser = localStorage.getItem('secureUser');
      if (secureUser) {
        const parsed = JSON.parse(secureUser);
        return parsed.email || null;
      }

      // Fallback : chercher dans googleUser
      const googleUser = localStorage.getItem('googleUser');
      if (googleUser) {
        const parsed = JSON.parse(googleUser);
        return parsed.email || null;
      }

      return null;
    } catch (error) {
return null;
    }
  }

  //  CORRECTION MAJEURE : Validation stricte du cache avec email
  private loadSubscriptionFromStorage(): void {
    try {
      const stored = localStorage.getItem('user_subscription');
      if (!stored) {
        
        this.initializeFreeSubscription();
        return;
      }
      
      const parsed = JSON.parse(stored);
      
  
      const currentUserEmail = this.getCurrentUserEmail();
      if (currentUserEmail && parsed.email && parsed.email !== currentUserEmail) {
     
        localStorage.removeItem('user_subscription');
        this.initializeFreeSubscription();
        return;
      }
      
 
      const cacheAge = Date.now() - (parsed.cachedAt || 0);
      const MAX_CACHE_AGE = 5 * 60 * 1000; // 5 minutes
      
      if (parsed.cachedAt && cacheAge > MAX_CACHE_AGE) {
     
        localStorage.removeItem('user_subscription');
        this.initializeFreeSubscription();
        return;
      }
      
      let tier: SubscriptionTier;
      
      if (parsed.subscription) {
        // ANCIEN FORMAT (UserSubscriptionData)
      
        
      
        if (
          parsed.subscription.planId?.includes('admin') && 
          !parsed.isAdmin
        ) {
        
          localStorage.removeItem('user_subscription');
          this.initializeFreeSubscription();
          return;
        }
        
        this.currentSubscription = parsed.subscription;
        tier = parsed.subscription.tier;
        
      } else if (parsed.tier) {
 
  
        
       
        if (parsed.tier === 'premium' && !parsed.verifiedBy) {
         
          localStorage.removeItem('user_subscription');
          this.initializeFreeSubscription();
          return;
        }
        
    
        if (parsed.plan_id?.includes('admin') && !parsed.isAdmin) {
         
          localStorage.removeItem('user_subscription');
          this.initializeFreeSubscription();
          return;
        }
        
        // Convertir le nouveau format vers l'ancien format interne
        tier = parsed.tier === 'premium' ? SubscriptionTier.PREMIUM : SubscriptionTier.FREE;
        
        //  CORRECTION : Ne pas utiliser 'admin_premium_lifetime' pour non-admin
        let planId = 'free_plan';
        if (tier === SubscriptionTier.PREMIUM) {
          if (parsed.isAdmin === true) {
            planId = 'admin_premium_lifetime';
          } else if (parsed.plan_id && !parsed.plan_id.includes('admin')) {
            planId = parsed.plan_id; // Utiliser le planId fourni (premium_monthly, premium_yearly)
          } else {
            planId = 'premium_monthly'; 
          }
        }
        
        this.currentSubscription = {
          tier: tier,
          features: this.getFeaturesByTier(tier),
          startDate: parsed.cachedAt || Date.now(),
          expiresAt: parsed.expires_at ? new Date(parsed.expires_at).getTime() : null,
          paymentId: parsed.verifiedBy || 'cache_restore',
          billingPeriod: parsed.billing_period || 'monthly',
          planId: planId
        };
        
      } else {
        // Format inconnu, initialiser en FREE
this.initializeFreeSubscription();
        return;
      }
      
      // Migration: Ajouter les nouveaux champs si ils n'existent pas
      if (!this.currentSubscription.billingPeriod) {
        this.currentSubscription.billingPeriod = 'monthly';
      }
      if (!this.currentSubscription.planId) {
        if (this.currentSubscription.tier === SubscriptionTier.PREMIUM) {
          this.currentSubscription.planId = this.currentSubscription.billingPeriod === 'yearly' 
            ? 'premium_yearly' 
            : 'premium_monthly';
        } else {
          this.currentSubscription.planId = 'free_plan';
        }
      }
      
    
     
      
    } catch (error) {
localStorage.removeItem('user_subscription');
      this.initializeFreeSubscription();
    }
  }

  // Initialiser un abonnement gratuit par défaut
  private initializeFreeSubscription(): void {
    this.serverVerified = false;
    this.currentSubscription = {
      tier: SubscriptionTier.FREE,
      features: this.getFeaturesByTier(SubscriptionTier.FREE),
      startDate: Date.now(),
      expiresAt: null,
      paymentId: 'free_plan',
      billingPeriod: 'monthly',
      planId: 'free_plan'
    };
 
  }

  private getFeaturesByTier(tier: SubscriptionTier): string[] {
    switch (tier) {
      case SubscriptionTier.FREE:
        return [
          'learn_unlimited',
          'quiz_unlimited',
          'revision_unlimited',
          'progress_stats',
          'categories_basic'
        ];
      case SubscriptionTier.PREMIUM:
        return [
          'learn_unlimited',
          'quiz_unlimited', 
          'revision_unlimited',
          'exercise_unlimited',
          'progress_stats',
          'categories_full',
          'grammar_full',
          'sentence_construction',
          'sentence_gap',
          'exercise_mode',
          'forge_game',
          'weaver_game',

          
          
        ];
      default:
        return [];
    }
  }

  public updateSubscription(
    tier: SubscriptionTier, 
    expiresAt?: number | null, 
    paymentId?: string,
    billingPeriod?: 'monthly' | 'yearly',
    planId?: string,
    serverVerified: boolean = false
  ): void {
    try {
      const now = Date.now();

      // Drapeau de confiance : posé par un appelant qui tient sa donnée du
      // serveur, conservé tant que l'abonnement reste Premium, effacé dès un
      // retour en FREE. Une mise à jour Premium venant du cache ne le pose pas.
      if (tier !== SubscriptionTier.PREMIUM) {
        this.serverVerified = false;
      } else if (serverVerified) {
        this.serverVerified = true;
      }
      
      const subscription: Subscription = {
        tier,
        features: this.getFeaturesByTier(tier),
        startDate: now,
        expiresAt: expiresAt || null,
        paymentId: paymentId || `local_${now}`,
        billingPeriod: billingPeriod || 'monthly',
        planId: planId || (tier === SubscriptionTier.PREMIUM ? 'premium_monthly' : 'free_plan')
      };

      const userData: UserSubscriptionData = {
        userId: this.userId,
        subscription,
        paymentHistory: this.getPaymentHistory()
      };

      localStorage.setItem('user_subscription', JSON.stringify(userData));
      this.currentSubscription = subscription;

     

      // ✅ CORRECTION : Émettre l'événement de mise à jour
      const event = new CustomEvent('subscriptionUpdated', {
        detail: {
          tier,
          planId: subscription.planId,
          billingPeriod: subscription.billingPeriod,
          isPremium: tier === SubscriptionTier.PREMIUM
        }
      });
      window.dispatchEvent(event);
    
    } catch (error) {
}
  }

  // Obtenir l'abonnement actuel
  public getCurrentSubscription(): Subscription | null {
    return this.currentSubscription;
  }

  // Obtenir le tier actuel
  public getCurrentTier(): SubscriptionTier {
    if (!this.currentSubscription) {
      return SubscriptionTier.FREE;
    }

    // Vérifier l'expiration pour les abonnements Premium
    if (this.currentSubscription.tier === SubscriptionTier.PREMIUM && 
        this.currentSubscription.expiresAt && 
        Date.now() > this.currentSubscription.expiresAt) {
    
      this.updateSubscription(
        SubscriptionTier.FREE,
        null,
        'expired',
        'monthly',
        'free_plan'
      );
      return SubscriptionTier.FREE;
    }

    // Premium chargé depuis le cache mais pas encore confirmé par le serveur
    // dans cette session → traité comme FREE pour l'accès aux fonctionnalités.
    if (this.currentSubscription.tier === SubscriptionTier.PREMIUM && !this.serverVerified) {
      return SubscriptionTier.FREE;
    }

    return this.currentSubscription.tier;
  }

  /** Premium indiqué par le cache local, même non encore confirmé (affichage seulement). */
  public isPremiumPendingVerification(): boolean {
    return this.currentSubscription?.tier === SubscriptionTier.PREMIUM && !this.serverVerified;
  }

  public getCurrentBillingPeriod(): 'monthly' | 'yearly' | null {
    const subscription = this.getCurrentSubscription();
    return subscription?.billingPeriod || null;
  }

  // Obtenir le planId actuel
  public getCurrentPlanId(): string | null {
    const subscription = this.getCurrentSubscription();
    return subscription?.planId || null;
  }


  public hasSpecificPlan(planId: string): boolean {
    const currentPlanId = this.getCurrentPlanId();
    return currentPlanId === planId;
  }


  public getCurrentPlanInfo(): { tier: SubscriptionTier; planId: string; billingPeriod: string; expiresAt: Date | null } {
    const subscription = this.getCurrentSubscription();
    if (!subscription) {
      return {
        tier: SubscriptionTier.FREE,
        planId: 'free_plan',
        billingPeriod: 'monthly',
        expiresAt: null
      };
    }

    return {
      tier: subscription.tier,
      planId: subscription.planId || 'free_plan',
      billingPeriod: subscription.billingPeriod || 'monthly',
      expiresAt: subscription.expiresAt ? new Date(subscription.expiresAt) : null
    };
  }

  // Vérifier si une fonctionnalité est disponible
  public hasFeature(feature: string): boolean {
    if (!this.currentSubscription) {
      return false;
    }

    const currentTier = this.getCurrentTier();
    if (currentTier === SubscriptionTier.FREE) {
      return false;
    }

    return this.currentSubscription.features.includes(feature);
  }


  public getAvailableFeatures(): string[] {
    const currentTier = this.getCurrentTier();
    return this.getFeaturesByTier(currentTier);
  }

  
  public isSubscriptionExpired(): boolean {
    if (!this.currentSubscription || !this.currentSubscription.expiresAt) {
      return false;
    }
    return Date.now() > this.currentSubscription.expiresAt;
  }

  // Obtenir le nombre de jours restants
  public getDaysRemaining(): number | null {
    if (!this.currentSubscription || 
        this.currentSubscription.tier !== SubscriptionTier.PREMIUM || 
        !this.currentSubscription.expiresAt) {
      return null;
    }

    const now = Date.now();
    const expiresAt = this.currentSubscription.expiresAt;
    
    if (now >= expiresAt) {
      return 0;
    }

    const diffMs = expiresAt - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  // Obtenir l'historique des paiements
  public getPaymentHistory(): Array<{ date: number; amount: number; plan: string; status: string; }> {
    try {
      const stored = localStorage.getItem('user_subscription');
      if (stored) {
        const userData: UserSubscriptionData = JSON.parse(stored);
        return userData.paymentHistory || [];
      }
    } catch (error) {
}
    return [];
  }

  // Ajouter un paiement Ã  l'historique
  public addPaymentToHistory(amount: number, plan: string, status: string = 'completed'): void {
    try {
      const currentHistory = this.getPaymentHistory();
      const newPayment = {
        date: Date.now(),
        amount,
        plan,
        status
      };

      currentHistory.push(newPayment);
      const limitedHistory = currentHistory.slice(-50);

      const userData: UserSubscriptionData = {
        userId: this.userId,
        subscription: this.currentSubscription!,
        paymentHistory: limitedHistory
      };

      localStorage.setItem('user_subscription', JSON.stringify(userData));
   
    } catch (error) {
}
  }


  public resetSubscription(): void {
    localStorage.removeItem('user_subscription');
    this.initializeFreeSubscription();
   
  }

  // Migrer d'un plan à  un autre
  public changePlan(newPlanId: string, billingPeriod: 'monthly' | 'yearly'): void {
    if (!this.currentSubscription) {
return;
    }

    let newExpiresAt = this.currentSubscription.expiresAt;
    if (this.currentSubscription.tier === SubscriptionTier.PREMIUM) {
      const now = Date.now();
      if (billingPeriod === 'monthly') {
        newExpiresAt = now + (30 * 24 * 60 * 60 * 1000);
      } else if (billingPeriod === 'yearly') {
        newExpiresAt = now + (365 * 24 * 60 * 60 * 1000);
      }
    }

    this.updateSubscription(
      this.currentSubscription.tier,
      newExpiresAt,
      `plan_change_${Date.now()}`,
      billingPeriod,
      newPlanId
    );

  }

  // Obtenir le statut de l'abonnement pour l'affichage
  public getSubscriptionStatus(): {
    tier: SubscriptionTier;
    planName: string;
    isActive: boolean;
    daysRemaining: number | null;
    expiresAt: Date | null;
    billingPeriod: string;
  } {
    const planInfo = this.getCurrentPlanInfo();
    const daysRemaining = this.getDaysRemaining();
    const isExpired = this.isSubscriptionExpired();

    let planName = 'Plan Gratuit';
    if (planInfo.tier === SubscriptionTier.PREMIUM) {
      planName = planInfo.billingPeriod === 'yearly' ? 'Premium Annuel' : 'Premium Mensuel';
    }

    return {
      tier: planInfo.tier,
      planName,
      isActive: planInfo.tier === SubscriptionTier.PREMIUM && !isExpired,
      daysRemaining,
      expiresAt: planInfo.expiresAt,
      billingPeriod: planInfo.billingPeriod
    };
  }

  public canAccessFeature(feature: string): { allowed: boolean; reason?: string } {
    const currentTier = this.getCurrentTier();
    
    if (this.isSubscriptionExpired()) {
      return {
        allowed: false,
        reason: 'Votre abonnement Premium a expiré. Renouvelez pour continuer.'
      };
    }

    if (!this.hasFeature(feature)) {
      if (currentTier === SubscriptionTier.FREE) {
        return {
          allowed: false,
          reason: 'Cette fonctionnalité est réservée aux abonnés Premium.'
        };
      }
    }

    return { allowed: true };
  }


  public clearCache(userEmail?: string): void {
   
    
    const currentEmail = this.getCurrentUserEmail();
    
    if (userEmail && currentEmail && userEmail !== currentEmail) {
     
    }
    
    localStorage.removeItem('user_subscription');
    this.initializeFreeSubscription();
    
  
  }

  public reloadSubscription(): void {
 
    this.loadSubscriptionFromStorage();
  }
 
  public debugSubscriptionState(): void {
    const currentEmail = this.getCurrentUserEmail();
    const cached = localStorage.getItem('user_subscription');
    let cachedEmail = null;
    
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        cachedEmail = parsed.email || null;
      } catch {}
    }
    
   
  }
}

// Singleton
const subscriptionService = new SubscriptionService();
export default subscriptionService;

// Exposer pour debugging uniquement en développement
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).subscriptionService = subscriptionService;
}